import './AmcInstances.scss';
import { SetStateAction, useEffect, useState } from 'react';
import { Button } from '@Components/Button';
import { Design, Intent, Size } from '@Components/designConstants';
import { Page } from '@Components/Page';
import { ToggleSwitch } from '@Components/ToggleSwitch';
import { useSetAreYouSure, useSetError } from '@src/redux/modals';
import { Mixpanel, MxE } from '@src/utils/mixpanelWrapper';
import { PAGE_NAMES } from '@Components/Page/page-constants';
import { ModalMode } from './amc-instances-types';
import { InstanceModal } from './components/InstanceModal';
import { InstancesBody } from './components/InstancesBody';
import { useAmcInstances } from './utils/useAmcInstances';

export const AmcInstances = () => {
  const { data: instances, isLoading, error } = useAmcInstances();
  const [modal, setModal] = useState<ModalMode | null>(null);
  // Not persisted, so a reload always returns to dry-run.
  const [live, setLive] = useState(false);
  const setError = useSetError();
  const setAreYouSure = useSetAreYouSure();

  useEffect(() => {
    if (error) {
      setError({
        title: "Couldn't load AMC instances",
        message: `Failed to load instances: ${error.message}`,
        reportError: error
      });
    }
  }, [error, setError]);

  // Turning live mode on needs confirmation; turning it off doesn't.
  const handleLiveChange = async (update: SetStateAction<boolean>) => {
    const nextLive = typeof update === 'function' ? update(live) : update;
    if (nextLive) {
      try {
        await setAreYouSure({
          title: 'Enable LIVE mode?',
          message:
            'Creates, edits, deletes, and advertiser changes will be sent to the real Tinuiti ' +
            'AMC account on Amazon. Leave this off to stay in safe dry-run mode.',
          okayText: 'Enable LIVE mode',
          cancelText: 'Stay in dry-run',
          intent: Intent.WARNING
        });
      } catch {
        return; // cancelled
      }
    }
    setLive(nextLive);
    if (nextLive) {
      Mixpanel.track(MxE.ActionTriggered, {
        page: PAGE_NAMES.AMC_INSTANCES,
        action: 'enable_live_mode'
      });
    }
  };

  return (
    <Page
      title={PAGE_NAMES.AMC_INSTANCES}
      pageType={PAGE_NAMES.AMC_INSTANCES}
      isLoading={isLoading}
      actions={
        <div className='amc-actions' data-testid='amc-page-actions'>
          <ToggleSwitch
            label={live ? 'LIVE mode' : 'Dry-run'}
            checked={live}
            design={live ? Design.BRAND : Design.NEUTRAL}
            onChange={handleLiveChange}
          />
          <Button size={Size.SM} onClick={() => setModal({ kind: 'create' })}>
            Create Instance
          </Button>
        </div>
      }
    >
      <div className='amc-instances-page'>
        <InstancesBody
          instances={instances}
          isLoading={isLoading}
          error={error}
          live={live}
          onEdit={instance => setModal({ kind: 'edit', instance })}
        />
      </div>

      {modal && <InstanceModal mode={modal} live={live} onClose={() => setModal(null)} />}
    </Page>
  );
};
